import { useState, useCallback, useEffect } from "react";
import { TourContext } from "@/contexts/tour-context";
const TOUR_DISMISSED_KEY = "tour_dismissed";
const TOUR_COMPLETED_KEY = "tour_completed";

export function TourProvider({
  children,
  isAuthenticated,
}: {
  children: React.ReactNode;
  isAuthenticated: boolean;
}) {
  const [isRunning, setIsRunning] = useState(false);
  const [shouldShowOffer, setShouldShowOffer] = useState(false);
  const [navigateFn, setNavigateFn] = useState<((page: string) => void) | null>(null);

  // Show the offer modal after login if user hasn't dismissed it permanently
  useEffect(() => {
    if (!isAuthenticated) return;
    const dismissed = localStorage.getItem(TOUR_DISMISSED_KEY);
    const completed = localStorage.getItem(TOUR_COMPLETED_KEY);
    if (!dismissed && !completed) {
      // Small delay so the app renders first
      const timer = setTimeout(() => setShouldShowOffer(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated]);

  const registerNavigate = useCallback((fn: (page: string) => void) => {
    setNavigateFn(() => fn);
  }, []);

  const navigateTo = useCallback(
    (page: string) => {
      navigateFn?.(page);
    },
    [navigateFn],
  );

  const startTour = useCallback(() => {
    setShouldShowOffer(false);
    // Navigate to the radar page so radar-specific steps have targets
    navigateFn?.("radar");
    // Short delay to let the page render before Joyride mounts
    setTimeout(() => setIsRunning(true), 300);
  }, [navigateFn]);

  const stopTour = useCallback(() => {
    setIsRunning(false);
    localStorage.setItem(TOUR_COMPLETED_KEY, "true");
  }, []);

  const dismissOffer = useCallback((dontShowAgain: boolean) => {
    setShouldShowOffer(false);
    if (dontShowAgain) {
      localStorage.setItem(TOUR_DISMISSED_KEY, "true");
    }
  }, []);

  const acceptOffer = useCallback(() => {
    setShouldShowOffer(false);
    // Navigate to radar page first, then start
    navigateFn?.("radar");
    setTimeout(() => setIsRunning(true), 300);
  }, [navigateFn]);

  return (
    <TourContext.Provider
      value={{
        isRunning,
        startTour,
        stopTour,
        shouldShowOffer,
        dismissOffer,
        acceptOffer,
        registerNavigate,
        navigateTo,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}
